import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Refresh,
  NetworkCheck,
  Settings,
  Computer,
  Security,
  History,
  PlayArrow,
  Stop
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';

const AtmServerManagement = () => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testConnectionDialogOpen, setTestConnectionDialogOpen] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [showApiKey, setShowApiKey] = useState({});
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [connectionLogs, setConnectionLogs] = useState([]);
  const [selectedServer, setSelectedServer] = useState(null);
  const [rawLogsDialogOpen, setRawLogsDialogOpen] = useState(false);
  const [selectedLogEntry, setSelectedLogEntry] = useState(null);
  const [testingServers, setTestingServers] = useState(new Set());

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    baseUrl: '',
    apiKey: '',
    isActive: true,
    username: '',
    password: '',
    timeout: 30,
    retryAttempts: 3,
    pollingInterval: 60
  });

  const { apiCall } = useApi();

  useEffect(() => {
    loadServers();
  }, []);

  const loadServers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiCall('/api/v1/atm-servers', 'GET');
      
      if (response && response.servers) {
        // Transform the response to match frontend expectations
        const transformedServers = response.servers.map(server => ({
          id: server.id,
          name: server.name,
          description: server.description,
          baseUrl: server.base_url,
          apiKey: server.apiKey,
          username: server.username,
          isActive: server.is_active,
          timeout: server.timeout_seconds,
          retryAttempts: server.retry_attempts,
          pollingInterval: server.polling_interval_seconds,
          status: server.status,
          lastConnection: server.last_connection_at ? new Date(server.last_connection_at) : null,
          // Remove transaction tracking - not reliable
          // totalTransactions: server.total_transactions || 0,
          // lastTransaction: server.last_transaction_at ? new Date(server.last_transaction_at) : null,
          createdAt: server.created_at ? new Date(server.created_at) : null,
          updatedAt: server.updated_at ? new Date(server.updated_at) : null
        }));
        
        setServers(transformedServers);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error loading ATM servers:', err);
      setError(err.message || 'Failed to load ATM servers');
      
      // Fallback to mock data if API fails
      const mockServers = [
        {
          id: 'mock-1',
          name: 'Primary ATM Server (Mock)',
          description: 'Main production ATM multiplexer server - API not available',
          baseUrl: 'http://10.1.1.100:8080',
          username: 'readonly',
          isActive: true,
          timeout: 30,
          retryAttempts: 3,
          pollingInterval: 60,
          status: 'disconnected',
          lastConnection: new Date(Date.now() - 60 * 60 * 1000),
          // totalTransactions: 1250,
          // lastTransaction: new Date(Date.now() - 5 * 60 * 1000)
        }
      ];
      setServers(mockServers);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (server = null) => {
    if (server) {
      setEditingServer(server);
      setFormData({
        name: server.name,
        description: server.description,
        baseUrl: server.baseUrl,
        apiKey: server.apiKey,
        isActive: server.isActive,
        username: server.username,
        password: '', // Don't populate password for security
        timeout: server.timeout,
        retryAttempts: server.retryAttempts,
        pollingInterval: server.pollingInterval
      });
    } else {
      setEditingServer(null);
      setFormData({
        name: '',
        description: '',
        baseUrl: '',
        apiKey: '',
        isActive: true,
        username: '',
        password: '',
        timeout: 30,
        retryAttempts: 3,
        pollingInterval: 60
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingServer(null);
    setFormData({
      name: '',
      description: '',
      baseUrl: '',
      apiKey: '',
      isActive: true,
      username: '',
      password: '',
      timeout: 30,
      retryAttempts: 3,
      pollingInterval: 60
    });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare the data payload
      const payload = {
        name: formData.name,
        description: formData.description,
        base_url: formData.baseUrl,
        apiKey: formData.apiKey,
        username: formData.username,
        password: formData.password,
        isActive: formData.isActive,
        timeout: parseInt(formData.timeout),
        retryAttempts: parseInt(formData.retryAttempts),
        pollingInterval: parseInt(formData.pollingInterval)
      };
      
      let response;
      
      if (editingServer) {
        // Update existing server
        response = await apiCall(`/api/v1/atm-servers/${editingServer.id}`, 'PUT', payload);
      } else {
        // Create new server
        response = await apiCall('/api/v1/atm-servers', 'POST', payload);
      }
      
      if (response && (response.message || response.server)) {
        // Reload the servers list to get the latest data
        await loadServers();
        handleCloseDialog();
        
        // Show success message
        console.log(editingServer ? 'Server updated successfully' : 'Server created successfully');
      } else {
        throw new Error(`Failed to ${editingServer ? 'update' : 'create'} ATM server`);
      }
      
    } catch (err) {
      console.error('Error saving ATM server:', err);
      setError(err.message || `Failed to ${editingServer ? 'update' : 'create'} ATM server`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (serverId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiCall(`/api/v1/atm-servers/${serverId}`, 'DELETE');
      
      if (response && (response.message || response.success)) {
        // Reload the servers list to get the latest data
        await loadServers();
        setDeleteDialogOpen(false);
        setEditingServer(null);
        console.log('Server deleted successfully');
      } else {
        throw new Error('Failed to delete ATM server');
      }
    } catch (err) {
      console.error('Error deleting ATM server:', err);
      setError(err.message || 'Failed to delete ATM server');
      setDeleteDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePolling = async (server) => {
    try {
      setLoading(true);
      setError(null);
      
      const newActiveState = !server.isActive;
      
      const response = await apiCall(`/api/v1/atm-servers/${server.id}`, 'PUT', {
        name: server.name,
        description: server.description,
        base_url: server.baseUrl,
        apiKey: server.apiKey,
        username: server.username,
        isActive: newActiveState,
        timeout: server.timeout,
        retryAttempts: server.retryAttempts,
        pollingInterval: server.pollingInterval
      });
      
      if (response && (response.message || response.server)) {
        // Reload the servers list to get the latest data
        await loadServers();
        console.log(`Server ${newActiveState ? 'started' : 'stopped'} successfully`);
      } else {
        throw new Error('Failed to toggle server polling');
      }
    } catch (err) {
      console.error('Error toggling server polling:', err);
      setError(err.message || 'Failed to toggle server polling');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (server, showDialog = true) => {
    try {
      // Track testing state
      setTestingServers(prev => new Set(prev).add(server.id));
      
      if (showDialog) {
        setConnectionTestResult({ status: 'testing', message: 'Testing connection...' });
        setTestConnectionDialogOpen(true);
      }
      
      const response = await apiCall(`/api/v1/atm-servers/${server.id}/test`, 'POST');
      
      if (response && response.connection_test) {
        const testResult = response.connection_test;
        
        if (testResult.success) {
          setConnectionTestResult({
            status: 'success',
            message: testResult.message,
            details: {
              responseTime: `${testResult.response_time_ms}ms`,
              endpoint: testResult.endpoint_tested,
              serverName: response.server_name
            }
          });
          
          // Reload servers to update status
          await loadServers();
        } else {
          setConnectionTestResult({
            status: 'error',
            message: testResult.message,
            details: {
              responseTime: testResult.response_time_ms ? `${testResult.response_time_ms}ms` : 'N/A',
              endpoint: testResult.endpoint_tested,
              serverName: response.server_name
            }
          });
        }
      } else {
        throw new Error('Failed to test connection');
      }
    } catch (err) {
      console.error('Error testing connection:', err);
      setConnectionTestResult({
        status: 'error',
        message: 'Test failed',
        details: { error: err.message }
      });
    } finally {
      // Remove from testing state
      setTestingServers(prev => {
        const newSet = new Set(prev);
        newSet.delete(server.id);
        return newSet;
      });
    }
  };

  const generateApiKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    handleFormChange('apiKey', result);
  };

  const handleViewLogs = async (server) => {
    try {
      setSelectedServer(server);
      setConnectionLogs([]);
      setLogsDialogOpen(true);
      
      const response = await apiCall(`/api/v1/atm-servers/${server.id}/logs`, 'GET');
      
      if (response && response.logs) {
        setConnectionLogs(response.logs);
      } else {
        setConnectionLogs([]);
      }
    } catch (err) {
      console.error('Error loading connection logs:', err);
      setConnectionLogs([]);
    }
  };

  const handleViewRawLog = (logEntry) => {
    setSelectedLogEntry(logEntry);
    setRawLogsDialogOpen(true);
  };

  const handleQuickTest = async (server) => {
    await handleTestConnection(server, false);
    // Refresh logs if the logs dialog is open for this server
    if (logsDialogOpen && selectedServer && selectedServer.id === server.id) {
      setTimeout(() => handleViewLogs(server), 1000); // Small delay to ensure log is saved
    }
  };

  const toggleApiKeyVisibility = (serverId) => {
    setShowApiKey(prev => ({
      ...prev,
      [serverId]: !prev[serverId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success';
      case 'disconnected': return 'error';
      case 'testing': return 'warning';
      default: return 'default';
    }
  };

  const formatDateTime = (date) => {
    if (!date) return 'Never';
    // Handle both Date objects and date strings
    const dateObj = date instanceof Date ? date : new Date(date);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(dateObj);
  };


  if (loading && servers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <Computer sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4">
            ATM Server Management
          </Typography>
        </Box>
      </Box>

      {/* Server Management Section */}
      <Box>
          {/* ATM Servers Tab */}
          <Box display="flex" gap={1} mb={2}>
            <Button
              startIcon={<Refresh />}
              onClick={loadServers}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={() => handleOpenDialog()}
            >
              Add Server
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Server List */}
          <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            ATM Server Endpoints
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Configure external ATM reporting API servers for live transaction ingestion
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Server Name</TableCell>
                  <TableCell>Base URL</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {servers.map((server) => (
                  <TableRow key={server.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {server.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {server.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {server.baseUrl}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={server.status}
                          color={getStatusColor(server.status)}
                          size="small"
                        />
                        {!server.isActive && (
                          <Chip label="Inactive" size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontFamily="monospace">
                          {server.apiKey ? (
                            showApiKey[server.id] 
                              ? server.apiKey 
                              : server.apiKey.length > 12 
                                ? server.apiKey.substring(0, 8) + '...' + server.apiKey.slice(-4)
                                : server.apiKey
                          ) : (
                            <em>No API key</em>
                          )}
                        </Typography>
                        {server.apiKey && (
                          <Tooltip title={showApiKey[server.id] ? "Hide API Key" : "Show API Key"}>
                            <IconButton
                              size="small"
                              onClick={() => toggleApiKeyVisibility(server.id)}
                              color={showApiKey[server.id] ? "primary" : "default"}
                            >
                              {showApiKey[server.id] ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(server.lastConnection)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        <Tooltip title="Test Connection (with dialog)">
                          <IconButton
                            size="small"
                            onClick={() => handleTestConnection(server)}
                            color="primary"
                            disabled={testingServers.has(server.id)}
                          >
                            <NetworkCheck />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Quick Test (no dialog)">
                          <IconButton
                            size="small"
                            onClick={() => handleQuickTest(server)}
                            color="secondary"
                            disabled={testingServers.has(server.id)}
                          >
                            {testingServers.has(server.id) ? <CircularProgress size={16} /> : <PlayArrow />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={server.isActive ? "Stop Polling" : "Start Polling"}>
                          <IconButton
                            size="small"
                            onClick={() => handleTogglePolling(server)}
                            color={server.isActive ? "error" : "success"}
                            disabled={loading}
                          >
                            {server.isActive ? <Stop /> : <PlayArrow />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Connection Logs">
                          <IconButton
                            size="small"
                            onClick={() => handleViewLogs(server)}
                            color="primary"
                          >
                            <History />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Server">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(server)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Server">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingServer(server);
                              setDeleteDialogOpen(true);
                            }}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {servers.length === 0 && !loading && (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">
                No ATM servers configured. Click "Add Server" to get started.
              </Typography>
            </Box>
          )}
          </CardContent>
        </Card>
      </Box>
      {/* Add/Edit Server Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Settings sx={{ mr: 1 }} />
            {editingServer ? 'Edit ATM Server' : 'Add ATM Server'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Server Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleFormChange('isActive', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Base URL"
                value={formData.baseUrl}
                onChange={(e) => handleFormChange('baseUrl', e.target.value)}
                placeholder="http://10.1.1.100:8080"
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Authentication" size="small" />
              </Divider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => handleFormChange('username', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => handleFormChange('password', e.target.value)}
                placeholder={editingServer ? 'Leave blank to keep current' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  label="API Key"
                  value={formData.apiKey}
                  onChange={(e) => handleFormChange('apiKey', e.target.value)}
                  required
                  type={showApiKey.form ? 'text' : 'password'}
                />
                <Tooltip title={showApiKey.form ? "Hide API Key" : "Show API Key"}>
                  <IconButton 
                    onClick={() => toggleApiKeyVisibility('form')}
                    color={showApiKey.form ? "primary" : "default"}
                  >
                    {showApiKey.form ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Tooltip>
                <Button
                  variant="outlined"
                  onClick={generateApiKey}
                  startIcon={<Security />}
                >
                  Generate
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Connection Settings" size="small" />
              </Divider>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Timeout (seconds)"
                type="number"
                value={formData.timeout}
                onChange={(e) => handleFormChange('timeout', parseInt(e.target.value))}
                inputProps={{ min: 5, max: 300 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Retry Attempts"
                type="number"
                value={formData.retryAttempts}
                onChange={(e) => handleFormChange('retryAttempts', parseInt(e.target.value))}
                inputProps={{ min: 0, max: 10 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Polling Interval (seconds)"
                type="number"
                value={formData.pollingInterval}
                onChange={(e) => handleFormChange('pollingInterval', parseInt(e.target.value))}
                inputProps={{ min: 10, max: 3600 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || !formData.baseUrl || !formData.apiKey}
          >
            {editingServer ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the ATM server "{editingServer?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => handleDelete(editingServer?.id)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Connection Test Dialog */}
      <Dialog open={testConnectionDialogOpen} onClose={() => setTestConnectionDialogOpen(false)}>
        <DialogTitle>Connection Test</DialogTitle>
        <DialogContent>
          {connectionTestResult && (
            <Box>
              <Alert severity={connectionTestResult.status === 'success' ? 'success' : connectionTestResult.status === 'testing' ? 'info' : 'error'}>
                {connectionTestResult.message}
              </Alert>
              
              {connectionTestResult.details && connectionTestResult.status === 'success' && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>Connection Details:</Typography>
                  <Typography variant="body2">Response Time: {connectionTestResult.details.responseTime}</Typography>
                  <Typography variant="body2">API Version: {connectionTestResult.details.apiVersion}</Typography>
                  <Typography variant="body2">Transaction Count: {connectionTestResult.details.transactionCount}</Typography>
                  <Typography variant="body2">Last Transaction: {formatDateTime(connectionTestResult.details.lastTransaction)}</Typography>
                </Box>
              )}
              
              {connectionTestResult.details && connectionTestResult.status === 'error' && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>Error Details:</Typography>
                  <Typography variant="body2" color="error">Error: {connectionTestResult.details.error}</Typography>
                  {connectionTestResult.details.suggestion && (
                    <Typography variant="body2">Suggestion: {connectionTestResult.details.suggestion}</Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestConnectionDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Connection Logs Dialog */}
      <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <History sx={{ mr: 1 }} />
            Connection Logs - {selectedServer?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {connectionLogs.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Response Time</TableCell>
                    <TableCell>Endpoint Tested</TableCell>
                    <TableCell>HTTP Status</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {connectionLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(log.attempted_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={log.connection_type} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={log.status} 
                          size="small" 
                          color={log.status === 'success' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={log.response_time_ms > 5000 ? 'bold' : 'normal'}>
                          {log.response_time_ms ? (
                            <span style={{ color: log.response_time_ms > 5000 ? '#f44336' : log.response_time_ms > 2000 ? '#ff9800' : '#4caf50' }}>
                              {log.response_time_ms}ms
                            </span>
                          ) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                          {log.endpoint_tested || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.http_status_code ? (
                            <Chip 
                              label={log.http_status_code} 
                              size="small" 
                              color={log.http_status_code >= 200 && log.http_status_code < 300 ? 'success' : 'error'}
                              variant="outlined"
                            />
                          ) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {log.error_message && (
                            <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                              <strong>Error:</strong> {log.error_message}
                            </Typography>
                          )}
                          {log.response_body_preview && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              <strong>Response:</strong> {log.response_body_preview.length > 100 ? 
                                `${log.response_body_preview.substring(0, 100)}...` : 
                                log.response_body_preview}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            By: {log.attempted_by || 'System'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Raw Log Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewRawLog(log)}
                            color="primary"
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">
                No connection logs found for this server.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Raw Log Details Dialog */}
      <Dialog open={rawLogsDialogOpen} onClose={() => setRawLogsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Visibility sx={{ mr: 1 }} />
            Raw Log Details - {selectedLogEntry && formatDateTime(selectedLogEntry.attempted_at)}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLogEntry && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>Basic Information</Typography>
                  <Box sx={{ backgroundColor: 'grey.50', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    <div><strong>ID:</strong> {selectedLogEntry.id}</div>
                    <div><strong>Server ID:</strong> {selectedLogEntry.server_id}</div>
                    <div><strong>Connection Type:</strong> {selectedLogEntry.connection_type}</div>
                    <div><strong>Status:</strong> {selectedLogEntry.status}</div>
                    <div><strong>Attempted At:</strong> {formatDateTime(selectedLogEntry.attempted_at)}</div>
                    <div><strong>Attempted By:</strong> {selectedLogEntry.attempted_by || 'System'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>Performance Metrics</Typography>
                  <Box sx={{ backgroundColor: 'grey.50', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    <div><strong>Response Time:</strong> {selectedLogEntry.response_time_ms || 'N/A'}ms</div>
                    <div><strong>HTTP Status:</strong> {selectedLogEntry.http_status_code || 'N/A'}</div>
                    <div><strong>Endpoint Tested:</strong> {selectedLogEntry.endpoint_tested || 'N/A'}</div>
                  </Box>
                </Grid>
                {selectedLogEntry.error_message && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom color="error">Error Information</Typography>
                    <Box sx={{ backgroundColor: '#ffebee', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem', color: 'error.main' }}>
                      {selectedLogEntry.error_message}
                    </Box>
                  </Grid>
                )}
                {selectedLogEntry.response_body_preview && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Response Body</Typography>
                    <Box sx={{ backgroundColor: 'grey.50', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem', maxHeight: '300px', overflow: 'auto' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {selectedLogEntry.response_body_preview}
                      </pre>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRawLogsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AtmServerManagement;